import dataProvider, { fetchAllData, sleep } from "../../dataProvider";
import { camelCase, uniq } from "lodash";
import capitalize from "lodash/capitalize";
import moment from "moment";
import {
  ccaDropOffLocationType,
  IncludeParentOptionEnum,
  IncludeSubsidyOptionEnum,
  IncludeSubsidySettingOptionEnum,
  ScheduleTypeEnum,
} from "./const";
import { CcaRateChartTypeEnum } from "../schools/const";

interface IUpdatedByWithFullName extends IUpdatedBy {
  fullName?: string;
}

interface IStudentSubsidyWithStatus extends Omit<IStudentSubsidy, "updatedBy"> {
  status: string;
  scheduleStartDate?: string;
  scheduleEndDate?: string;
  subsidyType?: string;
  description?: string;
  maximumStudentSubsidy?: number;
  invoiceIssuedAt?: string;
  subsidyAmount?: number;
  updatedBy?: IUpdatedByWithFullName;
}

export const exportStudentValidation = (values: formExportStudentValues) => {
  if (
    values.isIncludeStudentSchedule &&
    !values.scheduleType
  ) {
    return true;
  }

  if (
    values.isIncludeStudentSchedule &&
    values.scheduleType === ScheduleTypeEnum.Both &&
    (!values.academicYearId || !values.ccaAcademicYearId)
  ) {
    return true;
  }

  if (
    values.isIncludeStudentSchedule &&
    values.scheduleType === ScheduleTypeEnum.Regular &&
    !values.academicYearId
  ) {
    return true;
  }

  if (
    values.isIncludeStudentSchedule &&
    values.scheduleType === ScheduleTypeEnum.Cca &&
    !values.ccaAcademicYearId
  ) {
    return true;
  }

  if (
    values.isIncludeStudentSchedule &&
    (values.scheduleType === ScheduleTypeEnum.Both || values.scheduleType === ScheduleTypeEnum.Regular) &&
    values.isFilterScheduleByTerm &&
    !values.academicTermIds?.length
  ) {
    return true;
  }

  if (
    values.isIncludeStudentSchedule &&
    (values.scheduleType === ScheduleTypeEnum.Both || values.scheduleType === ScheduleTypeEnum.Cca) &&
    values.isFilterScheduleByCcaTerm &&
    !values.ccaAcademicTermIds?.length
  ) {
    return true;
  }

  if (
    values.isIncludeStudentSchedule &&
    !values.isIncludeEstimatedRevenue
  ) {
    return true;
  }

  if (
    values.isFilterByRegistrationDate &&
    (!values.startDate || !values.endDate)
  ) {
    return true;
  }

  if (
    values.isIncludeParent &&
    !values.includeParentOption
  ) {
    return true;
  }

  if (
    values.isFilterByStatus &&
    !values.status
  ) {
    return true;
  }

  if (
    values.isIncludeSubsidy &&
    !values.includeSubsidyOption
  ) {
    return true;
  }

  if (
    values.isIncludeSubsidy &&
    values.includeSubsidyOption === IncludeSubsidyOptionEnum.SubsidySetting &&
    !values.includeSubsidySettingOption
  ) {
    return true;
  }

  return values.isIncludeSubsidy &&
    values.includeSubsidyOption === IncludeSubsidyOptionEnum.SubsidyData &&
    (!values.rateChartIds?.length ||
      !values.subsidyDocumentStatus?.length ||
      !values.subsidyDocumentType?.length);
};

export const generateExportStudentFilename = (values: formExportStudentValues) => {
  // Initialize base prefix for the file name.
  // If date range is provided, include it in the prefix
  let fileNamePrefix = "Student Records";
  if (values.isFilterByStatus) {
    fileNamePrefix = `${capitalize(values.status)} Student Records`;
  }

  if (values.isFilterByRegistrationDate) {
    fileNamePrefix += ` from ${values.startDate} to ${values.endDate}`;
  }

  // Initialize an array to collect all parts to include in the file name based on toggle selections
  const suffixParts: string[] = [];

  // Add "Student Schedule" if the toggle is enabled
  if (values.isIncludeStudentSchedule) {
    suffixParts.push("Student Schedule");
  }

  // Add "Estimated Revenue" if the toggle is enabled
  if (values.isIncludeEstimatedRevenue === "yes") {
    suffixParts.push("Estimated Revenue");
  }

  // Add "Route Plan" if the toggle is enabled
  if (values.isIncludeActiveRoutePlan) {
    suffixParts.push("Route Plan");
  }

  // Add Parent info if the toggle is enabled
  // and based on parent option selection
  if (values.isIncludeParent) {
    if (values.includeParentOption === IncludeParentOptionEnum.ParentOnly) {
      suffixParts.push("Parent");
    } else if (values.includeParentOption === IncludeParentOptionEnum.ParentOnly) {
      suffixParts.push("Parent");
      suffixParts.push("Guardian");
    }
  }

  // Add Subsidy Setting info if the toggle is enabled
  // and based on subsidy setting option selection
  if (values.isIncludeSubsidy && values.includeSubsidyOption === IncludeSubsidyOptionEnum.SubsidySetting) {
    if (values.includeSubsidySettingOption === IncludeSubsidySettingOptionEnum.ActiveSubsidyOnly) {
      suffixParts.push("Active Subsidy Setting");
    } else if (values.includeSubsidySettingOption === IncludeSubsidySettingOptionEnum.AllSubsidySetting) {
      suffixParts.push("Subsidy Setting");
    }
  }

  // Add Subsidy Setting info if the toggle is enabled
  // and based on subsidy setting option selection
  if (values.isIncludeSubsidy && values.includeSubsidyOption === IncludeSubsidyOptionEnum.SubsidySetting) {
    suffixParts.push("Subsidy Data");
  }

  // Construct the suffix string if any toggle was enabled
  const suffix = suffixParts.length > 0 ? ` with ${suffixParts.join(" and ")}` : "";

  // Final filename composed of prefix, suffix, and the school name
  return `${fileNamePrefix}${suffix} - ${values.school.name}.csv`;
}

export const ccaScheduleDropOffTypeFormatter = (
  id: string,
  source: any,
): string => {
  const item = source.find((type: any) => type.id === id);
  return item ? item.name : "-";
};

/**
 * Determines the schedule type label based on whether it is "from school" or "to school."
 *
 * @param fromSchool - Indicates if the schedule is "from school." Defaults to false if null/undefined.
 * @param toSchool - Indicates if the schedule is "to school." Defaults to false if null/undefined.
 * @returns The label for the schedule type: "Both Way", "PM", "AM", or "None".
 */
export const getScheduleTypeLabel = (
  fromSchool?: boolean,
  toSchool?: boolean,
): string => {
  const isFromSchool = !!fromSchool; // convert null/undefined to false
  const isToSchool = !!toSchool; // convert null/undefined to false

  if (isFromSchool && isToSchool) {
    return "Both Way";
  } else if (isFromSchool && !isToSchool) {
    return "PM";
  } else if (!isFromSchool && isToSchool) {
    return "AM";
  }
  return "None";
};

/**
 * Returns a comma-separated string of days based on the `scheduleDetail` object.
 * @param scheduleDetail - The schedule details object from studentSchedule.
 * @returns A string of days (e.g., "Monday, Tuesday, Friday").
 */
export const getDaysOfWeek = (scheduleDetail: any): string => {
  // Define the mapping of schedule keys to their corresponding day labels.
  const daysOfWeek = [
    { key: "isMonday", label: "Monday" },
    { key: "isTuesday", label: "Tuesday" },
    { key: "isWednesday", label: "Wednesday" },
    { key: "isThursday", label: "Thursday" },
    { key: "isFriday", label: "Friday" },
  ];

  // Filter the days where the corresponding key in scheduleDetail is true,
  // map them to their labels, and join them into a comma-separated string.
  return daysOfWeek
    .filter((day) => scheduleDetail[day.key]) // Keep only the days that are true.
    .map((day) => day.label) // Extract the day names.
    .join(", "); // Join the names with a comma and a space.
};

/**
 * Extracts the CCA Area ID from a student's schedule based on the drop-off type.
 * - For "door_to_door" type: uses `ccaAreaDoorToDoorId`.
 * - For "fixed_location" type: returns the first area ID found from days where the student is scheduled (Monday to Friday).
 *
 * @param schedule The student schedule object containing dropOffType and detail
 * @returns The resolved area ID or null if not found
 */
const getCcaAreaIdFromSchedule = (schedule: any): number | null => {
  const detail = schedule.detail;
  const dropOffType = schedule.dropOffType;

  if (!detail || !dropOffType) {
    return null;
  }

  // Handle door-to-door drop-off type
  if (dropOffType === "door_to_door") {
    // Return door-to-door area ID if available
    return detail.ccaAreaDoorToDoorId ?? null;
  }

  // Handle fixed-location drop-off type
  if (dropOffType === "fixed_location") {
    // Days of the week to check
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

    for (const day of days) {
      // Check if the student is scheduled on that day (e.g., isMonday === true)
      if (detail[`is${day}`]) {
        // Get the corresponding ccaAreaXId (e.g., ccaAreaMondayId)
        const areaId = detail[`ccaArea${day}Id`];
        if (areaId != null) {
          // Return the first valid area ID found
          return areaId;
        }
      }
    }

    // No area ID found for any active day
    return null;
  }

  // Fallback for unknown drop-off types
  return null;
};


const formatCentsToDollar = (priceInCents: number): string =>
  `$${(priceInCents / 100).toFixed(2)}`;

/**
 * Retrieves the estimated revenue based on the schedule type.
 *
 * @param type - The schedule type (e.g., Regular or CCA).
 * @param studentData - The student object (Object response from studentMasterListData API)
 * @param schedule - The current schedule object
 * @param schoolCcaRateChartType - The school's setting to determine how CCA revenue is calculated
 * @returns The estimated revenue value if found, otherwise an empty string.
 */
const getEstimatedRevenue = (
  type: ScheduleTypeEnum,
  studentData: any,
  schedule: any,
  schoolCcaRateChartType: string
): string => {
  // Handle regular schedule
  if (type === ScheduleTypeEnum.Regular) {
    if (!studentData.regularRateChart) {
      return null;
    }

    const wayType = schedule.detail?.isFromSchool && schedule.detail?.isToSchool
      ? "two_way"
      : "one_way";

    const areaId = schedule.areaId;

    // Count how many days of the week the student is scheduled
    // `filter(Boolean)` removes all falsy values (e.g., false, null, undefined, 0, "")
    // So only the selected days (true) will remain in the array to determine frequency per week
    const days = [
      schedule.detail?.isMonday,
      schedule.detail?.isTuesday,
      schedule.detail?.isWednesday,
      schedule.detail?.isThursday,
      schedule.detail?.isFriday,
    ];
    const frequency = days.filter(Boolean).length;

    if (!areaId || !frequency) {
      return "Area or frequency on student schedule not found";
    }

    // Find matching item
    const item = studentData.regularRateChart?.items?.find(
      (i: any) => i.areaId === areaId && i.wayType === wayType && i.frequencyPerWeek === frequency
    );

    // Convert price in cents to dollar format if found
    return item?.price != null
      ? formatCentsToDollar(item.price)
      : null;
  } else if (type === ScheduleTypeEnum.Cca) { // Handle CCA schedule
    if (!studentData.ccaRateChart) {
      return null;
    }

    const ccaRateChartType = studentData.ccaRateChart?.ccaRateChartType;
    if (!ccaRateChartType || ccaRateChartType === CcaRateChartTypeEnum.NoCostCalculation) {
      return "$0.00";
    }

    switch (schoolCcaRateChartType) {
      case CcaRateChartTypeEnum.CostPerTripAndStudentArea: {
        // Ensure chart type matches
        if (ccaRateChartType !== CcaRateChartTypeEnum.CostPerTripAndStudentArea) {
          return "The latest CCA rate chart type is different with the current school setting";
        }

        // Get the correct CCA Area ID based on schedule details
        const ccaAreaId = getCcaAreaIdFromSchedule(schedule);
        if (!ccaAreaId) {
          return "CCA area on student schedule not found";
        }

        const matchedItem = studentData.ccaRateChart?.ccaRateChartAreaItems?.find(
          (item: any) => item.ccaAreaId === ccaAreaId
        );

        // Convert price in cents to dollar format if found
        return matchedItem?.price != null
          ? formatCentsToDollar(matchedItem.price)
          : null;
      }

      case CcaRateChartTypeEnum.CostPerWeek: {
        // Ensure chart type matches
        if (ccaRateChartType !== CcaRateChartTypeEnum.CostPerWeek) {
          return "The latest CCA rate chart type is different with the current school setting";
        }

        // Convert price in cents to dollar format if found
        return studentData.ccaRateChart?.ccaRateChartCostPerWeek?.price != null
          ? formatCentsToDollar(studentData.ccaRateChart.ccaRateChartCostPerWeek.price)
          : null;
      }

      case CcaRateChartTypeEnum.CostPerTripAndStudentType: {
        // Ensure chart type matches
        if (ccaRateChartType !== CcaRateChartTypeEnum.CostPerTripAndStudentType) {
          return "The latest CCA rate chart type is different with the current school setting";
        }

        // Check if student is a rider based on their regular status
        const isRider = studentData.regularStatus === "active";
        const priceInCents = isRider
          ? studentData.ccaRateChart?.ccaRateChartCostPerTrip?.riderPrice ?? ""
          : studentData.ccaRateChart?.ccaRateChartCostPerTrip?.nonRiderPrice ?? "";

        // Convert price in cents to dollar format if found
        return priceInCents != null
          ? `${formatCentsToDollar(priceInCents)} (${isRider ? "Rider" : "Non-rider"})`
          : null;
      }

      default:
        return "Invalid CCA rate chart type on school setting";
    }
  }

  return "Invalid student schedule type";
};

// Helper to merges student row data with parent and optional guardian info if toggles are enabled
const mergeRowWithParentAndGuardianIfNeeded = (
  row: any,
  parentData: any,
  guardianData: any,
  isIncludeParent: boolean,
  isIncludeGuardian: boolean
) => {
  if (!isIncludeParent) {
    return row;
  }

  const mergedRow = {
    ...row,
    parentId: parentData?.id ?? "",
    parentName: parentData ? `${parentData.firstName} ${parentData.lastName}` : "",
    mbcCustomerId: parentData?.mbcCustomerId ?? "",
    parentContact: parentData?.mobile ?? "",
    parentEmail: parentData?.email ?? "",
    parentStatus: parentData?.status ?? "",
  };

  if (isIncludeGuardian) {
    return {
      ...mergedRow,
      guardianName: guardianData?.name ?? "",
      guardianRelationship: guardianData?.relationship ?? "",
      guardianContact: guardianData?.contactNumber ?? "",
      guardianEmail: guardianData?.email ?? "",
    }
  }

  return mergedRow;
}

/**
 * Transform multiple student routes into rows.
 * - Groups routes by routeType (e.g., to_school, from_school, cca)
 * - Handles multiple routes of the same type by creating multiple rows
 * - Make sure at least one row exists even if routes are empty
 */
const transformRouteToRows = (
  routes: any[]
) => {
  // Make sure routes is always an array (fallback to empty array if null/undefined)
  const validRoutes = routes ?? [];

  // Group routes by routeType (e.g., to_school, from_school, cca)
  const grouped: Record<string, any[]> = {};
  validRoutes.forEach((r) => {
    if (!grouped[r.routeType]) {
      grouped[r.routeType] = [];
    }
    grouped[r.routeType].push(r);
  });

  // Get all route arrays (to_school, from_school, cca, etc.)
  const routeGroups = Object.values(grouped);

  // Get the length of each route array
  const groupSizes = routeGroups.map(arr => arr.length);

  // Find the maximum length across groups
  // Make sure at least 1 row (even if no routes exist)
  const maxRows = Math.max(...groupSizes, 1);

  const rows: any[] = [];

  // Build each row based on index i
  for (let i = 0; i < maxRows; i++) {
    // Initialize row with default empty values
    const row: any = {
      toSchoolRouteName: "",
      toSchoolStartTime: "",
      toSchoolEndTime: "",
      toSchoolAddress: "",
      toSchoolArriveTime: "",
      toSchoolOrder: "",
      toSchoolRemarks: "",

      fromSchoolRouteName: "",
      fromSchoolStartTime: "",
      fromSchoolEndTime: "",
      fromSchoolAddress: "",
      fromSchoolArriveTime: "",
      fromSchoolOrder: "",
      fromSchoolRemarks: "",

      ccaRouteName: "",
      ccaStartTime: "",
      ccaEndTime: "",
      ccaAddress: "",
      ccaArriveTime: "",
      ccaOrder: "",
      ccaRemarks: "",
    };

    // Fill the row with route data for each routeType
    Object.entries(grouped).forEach(([routeType, arr]) => {
      const route = arr[i]; // Take the i-th route of this type
      if (!route) return; // If no route at this index, skip

      const prefix = camelCase(routeType); // "to_school" → "toSchool"
      // Fill the row with data for this route
      row[`${prefix}RouteName`] = route.routeName;
      row[`${prefix}StartTime`] = route.startTime;
      row[`${prefix}EndTime`] = route.endTime;
      row[`${prefix}Address`] = `${route.routeLocation.blockNumber} ${route.routeLocation.street} ${route.routeLocation.unitNumber ?? ""} ${route.routeLocation.building ?? ""} ${route.routeLocation.postalCode}`;
      row[`${prefix}ArriveTime`] = route.routeLocation.arrivedAt;
      row[`${prefix}Order`] = route.routeLocation.order;
      row[`${prefix}Remarks`] = route.routeLocation.remark;
    });

    rows.push(row);
  }

  return rows;
};


// Helper to transforms a single student schedule into rows, adding route info if toggles are enabled
const transformScheduleToRows = (
  baseStudent: any,
  studentData: any,
  schedule: any,
  academicYear: any,
  academicTerm: any,
  ccaAcademicYear: any,
  ccaAcademicTerm: any,
  ccaDropOffLocationType: any,
  ccaFixedLocationDropOffs: any,
  ccaWaves: any,
  isIncludeActiveRoutePlan: boolean,
  isIncludeEstimatedRevenue: boolean,
  type: ScheduleTypeEnum,
  schoolCcaRateChartType: CcaRateChartTypeEnum,
) => {
  const isCca = type === ScheduleTypeEnum.Cca;
  const isRegular = type === ScheduleTypeEnum.Regular;

  // Determine which address columns to show (AM: pickUpAddress; PM: dropOffAddress)
  const showPickupAddress = isRegular && schedule.isTaking && schedule?.detail?.isToSchool;
  const showDropOffAddress = isRegular && schedule.isTaking && schedule?.detail?.isFromSchool;

  // Determine which CCA fixed location is selected as taking
  const isFixedLocationMonday = isCca && schedule.isTaking && schedule.dropOffType === "fixed_location" && schedule.detail.isMonday;
  const isFixedLocationTuesday = isCca && schedule.isTaking && schedule.dropOffType === "fixed_location" && schedule.detail.isTuesday;
  const isFixedLocationWednesday = isCca && schedule.isTaking && schedule.dropOffType === "fixed_location" && schedule.detail.isWednesday;
  const isFixedLocationThursday = isCca && schedule.isTaking && schedule.dropOffType === "fixed_location" && schedule.detail.isThursday;
  const isFixedLocationFriday = isCca && schedule.isTaking && schedule.dropOffType === "fixed_location" && schedule.detail.isFriday;

  // Build academic year and term fields
  const academicFields = {
    academicYearId: isRegular ? academicYear.id : "",
    academicYearName: isRegular ? academicYear.name : "",
    academicTermId: isRegular ? schedule.termId : "",
    academicTermName: isRegular ? academicTerm[schedule.termId] : "",
    ccaAcademicYearId: isCca ? ccaAcademicYear.id : "",
    ccaAcademicYearName: isCca ? ccaAcademicYear.name : "",
    ccaAcademicTermId: isCca ? schedule.termId : "",
    ccaAcademicTermName: isCca ? ccaAcademicTerm[schedule.termId] : "",
  };

  // Generate schedule label
  let scheduleLabel = "None";
  if (isRegular) {
    scheduleLabel = getScheduleTypeLabel(
      schedule?.detail?.isFromSchool,
      schedule?.detail?.isToSchool
    );
  } else if (isCca && schedule.isTaking) {
    scheduleLabel = ccaScheduleDropOffTypeFormatter(schedule.dropOffType, ccaDropOffLocationType);
  }

  // Build base row for the schedule
  const baseSchedule = {
    ...baseStudent,
    ...academicFields,
    isTaking: schedule.isTaking ? "Yes" : "No",
    scheduleType: isCca ? "CCA" : "Regular",
    schedule: scheduleLabel,
    datePeriod: schedule.isTaking
      ? `${moment(schedule.startDate).format("DD/MM/YYYY")} - ${moment(schedule.endDate).format("DD/MM/YYYY")}`
      : "",
    pickupAddress: showPickupAddress
      ? `${schedule.detail.blockNumber} ${schedule.detail.street} ${schedule.detail.unitNumber ?? ""} ${schedule.detail.building ?? ""} ${schedule.detail.postalCode}`
      : "",
    dropOffAddress: showDropOffAddress
      ? `${schedule.detail.blockNumber} ${schedule.detail.street} ${schedule.detail.unitNumber ?? ""} ${schedule.detail.building ?? ""} ${schedule.detail.postalCode}`
      : "",
    ccaDoorToDoor: isCca && schedule.isTaking && schedule.dropOffType === "door_to_door"
      ? `${schedule.detail.doorToDoorAddress.blockNumber} ${
          schedule.detail.doorToDoorAddress.street
        } ${schedule.detail.doorToDoorAddress.unitNumber ?? ""} ${
          schedule.detail.doorToDoorAddress.building ?? ""
        } ${schedule.detail.doorToDoorAddress.postalCode}`
      : "",
    ccaFixedLocationMonday: isFixedLocationMonday
      ? ccaFixedLocationDropOffs[schedule.detail.fixedLocationMondayId]
      : "",
    waveMonday: isFixedLocationMonday ? ccaWaves[schedule.detail.waveMondayId] : "",
    ccaFixedLocationTuesday: isFixedLocationTuesday
      ? ccaFixedLocationDropOffs[schedule.detail.fixedLocationTuesdayId]
      : "",
    waveTuesday: isFixedLocationTuesday ? ccaWaves[schedule.detail.waveTuesdayId] : "",
    ccaFixedLocationWednesday: isFixedLocationWednesday
      ? ccaFixedLocationDropOffs[schedule.detail.fixedLocationWednesdayId]
      : "",
    waveWednesday: isFixedLocationWednesday ? ccaWaves[schedule.detail.waveWednesdayId] : "",
    ccaFixedLocationThursday: isFixedLocationThursday
      ? ccaFixedLocationDropOffs[schedule.detail.fixedLocationThursdayId]
      : "",
    waveThursday: isFixedLocationThursday ? ccaWaves[schedule.detail.waveThursdayId] : "",
    ccaFixedLocationFriday: isFixedLocationFriday
      ? ccaFixedLocationDropOffs[schedule.detail.fixedLocationFridayId]
      : "",
    waveFriday: isFixedLocationFriday ? ccaWaves[schedule.detail.waveFridayId] : "",
    daysOfWeek: schedule.isTaking ? getDaysOfWeek(schedule.detail) : "",
  };

  // Conditionally include estimated revenue field
  if (isIncludeEstimatedRevenue) {
    if (schedule.isTaking) {
      // Set estimated revenue.
      // Show message "no rate chart found" if no estimated revenue found,
      // assuming the rate chart is empty
      const revenue = getEstimatedRevenue(type, studentData, schedule, schoolCcaRateChartType);
      baseSchedule.estimatedRevenue = revenue == null ? "No rate chart to estimate the revenue" : revenue;
    } else {
      // If the student is not taking the schedule, keep revenue field blank
      baseSchedule.estimatedRevenue = "";
    }
  }

  // If route plan is not included, return just schedule row
  if (!isIncludeActiveRoutePlan) {
    return [baseSchedule];
  }

  // Otherwise, expand schedule into multiple rows based on routes
  const routeRows = transformRouteToRows(studentData.studentRoutes);
  return routeRows.map((routeRow) => ({
    ...baseSchedule,
    ...routeRow
  }));
};

// Helper to merges student row data with subsidy setting info if toggles are enabled
const mergeRowWithSubsidySettingIfNeeded = (
  baseRows: any[],
  subsidiesToExport: IStudentSubsidyWithStatus[],
  isIncludeSubsidy: boolean,
  subsidyOption: IncludeSubsidyOptionEnum,
  parentData: any,
  guardianData: any,
  isIncludeParent: boolean,
  isIncludeGuardian: boolean
) => {
  // Initiate the base rows (could be schedules, routes, or a single student row)
  let finalRows = baseRows;

  // If subsidy setting is enabled, populate rows with subsidy setting
  if (isIncludeSubsidy && subsidyOption === IncludeSubsidyOptionEnum.SubsidySetting) {
    finalRows = transformSubsidySettingToRows(baseRows, subsidiesToExport);
  }

  // If subsidy setting is enabled, populate rows with subsidy setting
  if (isIncludeSubsidy && subsidyOption === IncludeSubsidyOptionEnum.SubsidyData) {
    finalRows = transformSubsidyDataToRows(baseRows, subsidiesToExport);
  }

  // After populate subsidy setting, merge parent/guardian info into each row
  return finalRows.map((row) =>
    mergeRowWithParentAndGuardianIfNeeded(row, parentData, guardianData, isIncludeParent, isIncludeGuardian)
  );
}

// Transforms the base student rows by attaching subsidy settings information.
// - If there are subsidies to export:
//   - For each row, duplicate it for every subsidy.
//   - Add formatted subsidy period (start and end dates).
//   - Add subsidy percentage as a string (e.g., "50%").
// - If there are no subsidies to export:
//   - Add empty values for subsidy period and percentage.
const transformSubsidySettingToRows = (
  baseRows: any[],
  subsidiesToExport: IStudentSubsidyWithStatus[]
) => {
  const result: any[] = [];

  baseRows.forEach((row) => {
    if (subsidiesToExport.length > 0) {
      // If the student has subsidies to export
      // For each subsidy, create a new row that keeps the student's info
      // but also adds subsidy period and percentage.
      // This means a single student row may become multiple rows
      // (one row for each subsidy).
      subsidiesToExport.forEach((subsidy) => {
        result.push({
          ...row,
          subsidySettingPeriod: `${moment
            .utc(subsidy.periodStartDate)
            .format("DD/MM/YYYY")} - ${moment
            .utc(subsidy.periodEndDate)
            .format("DD/MM/YYYY")}`,
          subsidySettingPercentage: `${subsidy.subsidyPercentage}%`,
          subsidySettingStatus: `${subsidy.status}`,
          subsidySettingUpdatedAt: `${moment
            .utc(subsidy.updatedAt)
            .format("DD/MM/YYYY")}`,
          subsidySettingUpdatedBy: `${subsidy.updatedBy.role} - ${subsidy.updatedBy.fullName}`,
        });
      });
    } else {
      // If there are no subsidies, still include the row
      // but leave the subsidy-related columns empty
      result.push({
        ...row,
        subsidySettingPeriod: "",
        subsidySettingPercentage: "",
        subsidySettingStatus: "",
        subsidySettingUpdatedAt: "",
        subsidySettingUpdatedBy: "",
      });
    }
  });

  // Return all rows
  return result;
}

const transformSubsidyDataToRows = (
  baseRows: any[],
  subsidiesToExport: IStudentSubsidyWithStatus[]
) => {
  const result: any[] = [];

  baseRows.forEach((row) => {
    if (subsidiesToExport.length > 0) {
      subsidiesToExport.forEach((subsidy) => {
        result.push({
          ...row,
          subsidySettingPeriod: `${moment
            .utc(subsidy.periodStartDate)
            .format("DD/MM/YYYY")} - ${moment
            .utc(subsidy.periodEndDate)
            .format("DD/MM/YYYY")}`,
          subsidyType: subsidy.subsidyType,
          maxBusFareCap: formatCentsToDollar(subsidy.maximumStudentSubsidy),
          issueDate: `${moment
            .utc(subsidy.invoiceIssuedAt)
            .format("DD/MM/YYYY")}`,
          description: subsidy.description,
          subsidyPercentage: `${subsidy.subsidyPercentage}%`,
          subsidyValue: formatCentsToDollar(subsidy.subsidyAmount),
        });
      });
    } else {
      // If there are no subsidies, still include the row
      // but leave the subsidy-related columns empty
      result.push({
        ...row,
        subsidySettingPeriod: "",
        subsidyType: "",
        maxBusFareCap: "",
        issueDate: "",
        description: "",
        subsidyPercentage: "",
        subsidyValue: "",
      });
    }
  });

  // Return all rows
  return result;
}

/**
 * Transforms a student record into one or more rows
 * for CSV output, based on the toggled options (e.g., parent info,
 * student schedules, and route plans).
 *
 * Depending on the provided flags, this function:
 * - Includes parent and guardian data if requested
 * - Expands each student schedule into one or more rows
 * - Attaches active route plan data to schedule or base row
 * - A base row if no schedules/routes exist
 *
 * @param scheduleType - Selected schedule type to export (Regular, CCA, or Both)
 * @param parentOption - Enum option to include parent only or parent with guardian
 * @param subsidySettingOption - Enum option to include active subsidy setting only or all subsidy setting
 * @param includeSubsidyOption - Enum option to include subsidy setting or subsidy data
 * @param levels - Lookup map of levelId -> grade name (Record<number, string>)
 * @param levelGroups - Lookup map of levelGroupId -> class name (Record<number, string>)
 * @param academicYear - Selected academic year for regular schedules
 * @param ccaAcademicYear - Selected academic year for CCA schedules
 * @param academicTerm - Lookup map of termId -> term name (for regular)
 * @param ccaAcademicTerm - Lookup map of ccaTermId -> term name (for CCA)
 * @param ccaFixedLocationDropOffs - Lookup map of fixedLocationId -> fixed location name
 * @param ccaWaves - Lookup map of waveId -> wave name
 * @param admins - Lookup map of adminId -> admin full name
 * @param record - The student record to be transformed
 * @param schoolCode - MBC code of the student's school
 * @param schoolCcaRateChartType - CCA Rate Chart Type of the student's school
 * @param subsidyRateChartIds - Rate Chart Ids used when include student subsidy data
 * @param subsidyDocumentTypes - Commercial Document types used when include student subsidy data
 * @param subsidyDocumentStatus - Commercial Document status used when include student subsidy data
 * @param isIncludeStudentSchedule - Flag to include student schedule rows
 * @param isIncludeParent - Flag to include parent and/or guardian data
 * @param isIncludeActiveRoutePlan - Flag to include route plan info in the rows
 * @param isIncludeEstimatedRevenue - Flag to include estimated revenue info in the rows
 * @param isIncludeSubsidy - Flag to include student subsidy setting info in the rows
 * @param isFilterScheduleByTerm - Flag to filter the rows by selected schedule term
 * @param isFilterScheduleByCcaTerm - Flag to filter the rows by selected CCA schedule term
 * @param academicTermIds - Array of selected schedule terms
 * @param ccaAcademicTermIds - Array of selected CCA schedule terms
 * @param signal - AbortSignal to support cancellation during async processing
 * @returns An array of exportable rows (CSV-ready)
 */
const studentReportTransform = async (
  scheduleType: ScheduleTypeEnum,
  parentOption: IncludeParentOptionEnum,
  subsidySettingOption: IncludeSubsidySettingOptionEnum,
  includeSubsidyOption: IncludeSubsidyOptionEnum,
  levels: any,
  levelGroups: any,
  academicYear: any,
  ccaAcademicYear: any,
  academicTerm: any,
  ccaAcademicTerm: any,
  ccaFixedLocationDropOffs: any,
  ccaWaves: any,
  admins: any,
  record: IStudent,
  schoolCode: string,
  schoolCcaRateChartType: CcaRateChartTypeEnum,
  subsidyRateChartIds: number[],
  subsidyDocumentTypes: string[],
  subsidyDocumentStatus: string[],
  isIncludeStudentSchedule: boolean,
  isIncludeParent: boolean,
  isIncludeActiveRoutePlan: boolean,
  isIncludeEstimatedRevenue: boolean,
  isIncludeSubsidy: boolean,
  isFilterScheduleByTerm: boolean,
  isFilterScheduleByCcaTerm: boolean,
  academicTermIds: number[],
  ccaAcademicTermIds: number[],
  signal: AbortSignal
) => {
  // throw an error if the operation is canceled
  if (signal.aborted) {
    throw new Error("Operation aborted");
  }

  try {
    // Prepare query parameters based on enabled toggles
    let queryFilter: any = {
      StudentId: record.id,
    }

    if (isIncludeStudentSchedule) {
      queryFilter = {
        ...queryFilter,
        IsIncludeStudentSchedule: true,
        AcademicYearId: academicYear.id,
        CcaAcademicYearId: ccaAcademicYear.id,
        ScheduleType: scheduleType,
      }
    }

    if (isIncludeActiveRoutePlan) {
      queryFilter.IsIncludeActiveRoutePlan = true;
    }

    if (isIncludeParent) {
      queryFilter.IsIncludeParent = true;
    }

    if (isIncludeEstimatedRevenue) {
      queryFilter.IsIncludeEstimatedRevenue = true;
    }

    if (isIncludeSubsidy && includeSubsidyOption === IncludeSubsidyOptionEnum.SubsidyData) {
      queryFilter = {
        ...queryFilter,
        IsIncludeSubsidyData: true,
        SubsidyRateChartIds: subsidyRateChartIds,
        SubsidyDocumentTypeIn: subsidyDocumentTypes,
        SubsidyDocumentStatusIn: subsidyDocumentStatus
      }
    }

    // Get student data with relevant relationships
    const { data: studentData } = await dataProvider.customGetList("studentMasterListData", {
      filter: queryFilter
    });

    let { regularStudentSchedules: regularSchedules, ccaStudentSchedules: ccaSchedules } = studentData;
    if (
      (scheduleType === ScheduleTypeEnum.Regular || scheduleType === ScheduleTypeEnum.Both) &&
      isFilterScheduleByTerm &&
      academicTermIds?.length > 0
    ) {
      regularSchedules = studentData.regularStudentSchedules.filter((s: IStudentSchedule) => academicTermIds.includes(s.termId))
    }

    if (
      (scheduleType === ScheduleTypeEnum.Cca || scheduleType === ScheduleTypeEnum.Both) &&
      isFilterScheduleByCcaTerm &&
      ccaAcademicTermIds?.length > 0
    ) {
      ccaSchedules = studentData.ccaStudentSchedules.filter((s: ICcaStudentSchedule) => ccaAcademicTermIds.includes(s.termId))
    }

    // get main student address
    const mainAddresses = await fetchAllData("studentAddresses", {
      StudentId: record.id,
      IsMain: true,
    });

    // Assuming, student has at least one main address
    const mainAddress = mainAddresses[0];
    const baseStudent = {
      schoolCode,
      studentId: record.id,
      studentExternalId: record.externalId ?? "",
      studentName: `${record.firstName} ${record.lastName}`,
      gender: capitalize(record.gender),
      grade: levels[record.levelId] ?? "",
      class: levelGroups[record.levelGroupId] ?? "",
      studentStatus: record.status,
      studentMainAddress: `${mainAddress?.blockNumber ?? ""} ${mainAddress?.street ?? ""} ${
        mainAddress?.unitNumber ?? ""
      } ${mainAddress?.building ?? ""} ${mainAddress?.postalCode ?? ""}`,
    }

    let subsidiesToExport: IStudentSubsidyWithStatus[] = [];
    if (isIncludeSubsidy && includeSubsidyOption === IncludeSubsidyOptionEnum.SubsidySetting) {
      const studentSubsidies = await fetchAllData(
        "studentSubsidies",
        {
          StudentId: record.id,
        },
        {
          field: "updatedAt",
          order: "desc",
        },
      );

      // Add status to each subsidy: "Active" if matches activeStudentSubsidyId, otherwise "Archive"
      // and add updatedBy.fullName
      const studentSubsidiesWithStatus = studentSubsidies.map(
        (subsidy: IStudentSubsidy) => {
          // Lookup admin fullName from admins array
          const admin = subsidy.updatedBy?.id
            ? admins[subsidy.updatedBy?.id]
            : null;

          return {
            ...subsidy,
            status: subsidy.id === record.activeStudentSubsidyId ? "Active" : "Archive",
            updatedBy: subsidy.updatedBy
              ? {
                ...subsidy.updatedBy,
                fullName: admin ?? "", // add fullName if found
              }
              : null,
          }
        }
      );

      subsidiesToExport =
        subsidySettingOption === IncludeSubsidySettingOptionEnum.ActiveSubsidyOnly
          ? studentSubsidiesWithStatus.filter(
            (subsidy: IStudentSubsidyWithStatus) => subsidy.id === record.activeStudentSubsidyId
          )
          : studentSubsidiesWithStatus;
    }

    if (isIncludeSubsidy && includeSubsidyOption === IncludeSubsidyOptionEnum.SubsidyData) {
      subsidiesToExport = studentData.studentSubsidies || [];
    }

    // Prepare parent and guardian data (if applicable)
    let parentData: any = null;
    let guardianData: any = null;
    if (isIncludeParent) {
      parentData = studentData.parent;
      if (parentOption === IncludeParentOptionEnum.ParentWithGuardian) {
        guardianData = parentData?.alternateContacts?.[0] ?? null;
      }
    }

    const isIncludeGuardian = parentOption === IncludeParentOptionEnum.ParentWithGuardian;

    // Handle Student Schedule Regular & CCA (if applicable)
    if (isIncludeStudentSchedule) {
      const scheduleRows: any[] = [];

      // Process each regular schedule
      regularSchedules.forEach((regularSchedule: any) => {
        const regularSchedules = transformScheduleToRows(
          baseStudent,
          studentData,
          regularSchedule,
          academicYear,
          academicTerm,
          ccaAcademicYear,
          ccaAcademicTerm,
          ccaDropOffLocationType,
          ccaFixedLocationDropOffs,
          ccaWaves,
          isIncludeActiveRoutePlan,
          isIncludeEstimatedRevenue,
          ScheduleTypeEnum.Regular,
          schoolCcaRateChartType
        );

        scheduleRows.push(...regularSchedules);
      });

      // Process each CCA schedule
      ccaSchedules.forEach((ccaSchedule: any) => {
        const ccaSchedules = transformScheduleToRows(
          baseStudent,
          studentData,
          ccaSchedule,
          academicYear,
          academicTerm,
          ccaAcademicYear,
          ccaAcademicTerm,
          ccaDropOffLocationType,
          ccaFixedLocationDropOffs,
          ccaWaves,
          isIncludeActiveRoutePlan,
          isIncludeEstimatedRevenue,
          ScheduleTypeEnum.Cca,
          schoolCcaRateChartType
        );

        scheduleRows.push(...ccaSchedules);
      });

      // If no schedules exist, add a empty row
      if (
        regularSchedules.length === 0 &&
        ccaSchedules.length === 0
      ) {
        const emptyScheduleRow: any = {
          ...baseStudent,
          academicYearId: "",
          academicYearName: "",
          ccaAcademicYearId: "",
          ccaAcademicYearName: "",
          academicTermId: "",
          academicTermName: "",
          ccaAcademicTermId: "",
          ccaAcademicTermName: "",
          isTaking: "No",
          scheduleType: "None",
          schedule: "None",
          datePeriod: "",
          pickupAddress: "",
          dropOffAddress: "",
          ccaDoorToDoor: "",
          ccaFixedLocationMonday: "",
          waveMonday: "",
          ccaFixedLocationTuesday: "",
          waveTuesday: "",
          ccaFixedLocationWednesday: "",
          waveWednesday: "",
          ccaFixedLocationThursday: "",
          waveThursday: "",
          ccaFixedLocationFriday: "",
          waveFriday: "",
          daysOfWeek: "",
        };

        // Only add empty estimatedRevenue if the toggle is enabled
        if (isIncludeEstimatedRevenue) {
          emptyScheduleRow.estimatedRevenue = "";
        }

        // If include route plan toggle is On
        // Add route data if available, or push empty route field
        if (isIncludeActiveRoutePlan) {
          const routeRowsResponse = transformRouteToRows(studentData.studentRoutes);
          routeRowsResponse.forEach((routeRow: any) => {
            scheduleRows.push({
              ...baseStudent,
              ...routeRow
            });
          });
        } else {
          scheduleRows.push(emptyScheduleRow);
        }
      }

      return mergeRowWithSubsidySettingIfNeeded(
        scheduleRows,
        subsidiesToExport,
        isIncludeSubsidy,
        includeSubsidyOption,
        parentData,
        guardianData,
        isIncludeParent,
        isIncludeGuardian
      );
    }

    // If include route plan toggle is On
    // Handle export of only route data (no schedule)
    if (isIncludeActiveRoutePlan) {
      const routeRows: any[] = [];
      const routeRowsResponse = transformRouteToRows(studentData.studentRoutes);
      routeRowsResponse.forEach((routeRow: any) => {
        routeRows.push({
          ...baseStudent,
          ...routeRow
        });
      });

      return mergeRowWithSubsidySettingIfNeeded(
        routeRows,
        subsidiesToExport,
        isIncludeSubsidy,
        includeSubsidyOption,
        parentData,
        guardianData,
        isIncludeParent,
        isIncludeGuardian
      );
    }

    // if only parent/guardian data is included
    return mergeRowWithSubsidySettingIfNeeded(
      [baseStudent],
      subsidiesToExport,
      isIncludeSubsidy,
      includeSubsidyOption,
      parentData,
      guardianData,
      isIncludeParent,
      isIncludeGuardian
    );
  } catch (error) {
    throw new Error(
      `Failed to fetch student with related data. ${error.message}. Please try again in a few moments`
    );
  }
};

/**
 * Processes records for CSV export, chunking data and fetching additional information as needed
 * based on the specified resource type. A callback function is called to track loading progress.
 *
 * @param body - A form data object.
 * @param records - An array of records to process.
 * @param callback - A function to handle the loading state and progress updates.
 * @returns An array containing processed data ready for CSV export.
 */
export const processStudentRecordsForCsv = async (
  body: any,
  records: any[],
  callback: (progressUpdate: {
    isLoading?: boolean;
    loadedRecordCount: number;
    totalRecords: number;
  }) => void,
) => {
  // Split the records array into chunks of the specified size
  const chunkSize = 100;
  const chunks = Array.from(
    { length: Math.ceil(records.length / chunkSize) },
    (_, i) => records.slice(i * chunkSize, i * chunkSize + chunkSize),
  );

  let processedData: any[] = [];

  // Create an abort controller to handle cancellation of tasks
  const abortController = new AbortController();
  const signal = abortController.signal;

  let academicTerm = {};
  let academicYear = {};
  let ccaAcademicYear = {};
  let ccaAcademicTerm = {};
  let ccaFixedLocationDropOffs = {};
  let ccaWaves = {};
  let admins = {};

  const studentLevels = records
    .map((record: any) => record.levelId)
    .filter((item) => item);
  const studentLevelGroups = records
    .map((record: any) => record.levelGroupId)
    .filter((item) => item);

  // Extract unique levelId and levelGroupId
  const levelIds = uniq(studentLevels);
  const levelGroupIds = uniq(studentLevelGroups);

  // Get Levels and Level Groups
  // then Using Record<number, string> to map id to name
  const levelResponse = await dataProvider.getMany("levels", {
    ids: levelIds,
  });
  const levelGroupResponse = await dataProvider.getMany("levelGroups", {
    ids: levelGroupIds,
  });

  const levels = Object.fromEntries(
    levelResponse.data.map((level: ILevel) => [level.id, level.name])
  );
  const levelGroups = Object.fromEntries(
    levelGroupResponse.data.map((levelGroup: ILevelGroup) => [
      levelGroup.id,
      levelGroup.name,
    ])
  );

  if (body.isIncludeStudentSchedule) {
    if (
      body.scheduleType === ScheduleTypeEnum.Regular ||
      body.scheduleType === ScheduleTypeEnum.Both
    ) {
      // Get academic year detail and assign academicYearLabel
      const academicYearResponse = await dataProvider.getOne(
        "academicYears",
        {
          id: body.academicYearId,
        }
      );
      academicYear = academicYearResponse.data;

      const academicTermResponse = await fetchAllData(
        "academicTerms",
        {
          AcademicYearId: body.academicYearId
        }
      );
      academicTerm = Object.fromEntries(
        academicTermResponse.map((academicTerm: IAcademicTerm) => [
          academicTerm.id,
          academicTerm.name,
        ])
      );
    }

    if (
      body.scheduleType === ScheduleTypeEnum.Cca ||
      body.scheduleType === ScheduleTypeEnum.Both
    ) {
      // Get academic year detail and assign academicYearLabel

      const ccaAcademicYearResponse = await dataProvider.getOne(
        "ccaAcademicYears",
        {
          id: body.ccaAcademicYearId,
        }
      );
      ccaAcademicYear = ccaAcademicYearResponse.data;

      const ccaFixedLocationResponse = await fetchAllData(
        "ccaFixedLocationDropOffs",
        {
          SchoolId: body.school.id
        }
      );
      ccaFixedLocationDropOffs = Object.fromEntries(
        ccaFixedLocationResponse.map((fixedLocation: ICcaFixedLocationDropOff) => [
          fixedLocation.id,
          fixedLocation.name,
        ]),
      );

      const ccaAcademicTermResponse = await fetchAllData(
        "ccaAcademicTerms",
        {
          CcaAcademicYearId: body.ccaAcademicYearId
        }
      );
      ccaAcademicTerm = Object.fromEntries(
        ccaAcademicTermResponse.map(
          (ccaAcademicTerm: ICcaAcademicTerm) => [
            ccaAcademicTerm.id,
            ccaAcademicTerm.name,
          ]
        )
      );

      const ccaWaveResponse = await fetchAllData(
        "busSchedules",
        {
          SchoolId: body.school.id,
        }
      );
      ccaWaves = Object.fromEntries(
        ccaWaveResponse.map((ccaWave: IBusSchedule) => [
          ccaWave.id,
          ccaWave.name,
        ]),
      );
    }
  }

  if (body.isIncludeSubsidy) {
    // Used to show the updatedBy field for subsidy setting related column
    const adminResponse = await fetchAllData("admins");
    admins = Object.fromEntries(
      adminResponse.map((admin) => [
        admin.id,
        admin.fullName,
      ])
    );
  }

  // Loop over each chunk to process records in manageable batches
  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
    const chunk = chunks[chunkIndex];

    // Process all records within the chunk concurrently
    const chunkResults = await Promise.all(
      chunk.map(async (record, recordIndex) => {
        // Exit if the operation is canceled
        if (signal.aborted) return;

        // Delay each request by 100ms * recordIndex within the chunk
        await sleep(recordIndex * 100);

        // Calculate the current record index for progress tracking and
        // update the progress using the callback
        const currentIndex = chunkIndex * chunkSize + recordIndex + 1;
        callback({
          loadedRecordCount: currentIndex,
          totalRecords: records.length,
        });

        // Process records based on the export type
        const scheduleType = body.isIncludeStudentSchedule ? body.scheduleType : null;
        const parentDataOption = body.isIncludeParent ? body.includeParentOption : null;
        const subsidyDataOption = body.isIncludeSubsidy ? body.includeSubsidyOption : null;
        const subsidySettingDataOption = body.isIncludeSubsidy && body.includeSubsidyOption === IncludeSubsidyOptionEnum.SubsidySetting ? body.includeSubsidySettingOption : null;
        return studentReportTransform(
          scheduleType,
          parentDataOption,
          subsidySettingDataOption,
          subsidyDataOption,
          levels,
          levelGroups,
          academicYear,
          ccaAcademicYear,
          academicTerm,
          ccaAcademicTerm,
          ccaFixedLocationDropOffs,
          ccaWaves,
          admins,
          record,
          body.school.mbcCode,
          body.school.ccaRateChartType,
          body.rateChartIds,
          body.subsidyDocumentType,
          body.subsidyDocumentStatus,
          body.isIncludeStudentSchedule,
          body.isIncludeParent,
          body.isIncludeActiveRoutePlan,
          body.isIncludeEstimatedRevenue === "yes",
          body.isIncludeSubsidy,
          body.isFilterScheduleByTerm,
          body.isFilterScheduleByCcaTerm,
          body.academicTermIds,
          body.ccaAcademicTermIds,
          signal
        );
      }),
    );

    // Append processed chunk results to the main processed data array
    processedData = [...processedData, ...chunkResults.flat()];
  }

  // Return the fully processed data for CSV export
  // And add sequential "no" column in the first column
  return processedData.map((record, i) => {
    return {
      no: i + 1,
      ...record,
    }
  });
};
